import forEach from 'lodash/forEach';
import defaults from 'lodash/defaults';
import MobileDetect from 'mobile-detect';

const isMobileDevice = () =>
  typeof window !== 'undefined' &&
  new MobileDetect(window.navigator.userAgent).mobile();

const DEFAULT_CONFIG = googleMaps => ({
  zoom: 14,
  disableDefaultUI: true,
  disableDoubleClickZoom: false,
  mapTypeControl: false,
  fullscreenControlOptions: {
    position: googleMaps.ControlPosition.RIGHT_BOTTOM
  },
  zoomControl: !isMobileDevice(),
  zoomControlOptions: {
    position: googleMaps.ControlPosition.LEFT_BOTTOM
  },
  minZoom: 5,
  draggable: true,
  scrollwheel: true,
  gestureHandling: 'greedy'
});

class MapsAPI {
  constructor(mapId, options) {
    this.options = options || {};
    this.markers = {};
    this.circles = {};
    this.mapId = mapId;
    this.googleMaps = window.google.maps;

    this.map = new this.googleMaps.Map(
      document.getElementById(mapId),
      defaults(this.options, DEFAULT_CONFIG(this.googleMaps))
    );

    if (this.options.transitLayer) {
      const tL = new this.googleMaps.TransitLayer();
      tL.setMap(this.map);
    }
  }

  addMarker = (markerId, opts) => {
    if (this.markers[markerId]) {
      this.removeMarker(markerId);
    }

    const markerOpts = {
      position: {
        lat: parseFloat(opts.lat),
        lng: parseFloat(opts.lng)
      },
      map: this.map,
      icon: opts.icon,
      label: opts.label
    };

    if (opts.zIndex) {
      markerOpts.zIndex = opts.zIndex;
    }

    this.markers[markerId] = new this.googleMaps.Marker(markerOpts);
  };

  removeMarker = markerId => {
    if (this.markers[markerId]) {
      this.googleMaps.event.clearInstanceListeners(this.markers[markerId]);
      this.markers[markerId].setMap(null);
      this.markers[markerId] = null;
    }
  };

  isMarkerDefined = markerId => !!this.markers[markerId];

  removeAllMarkers = () => {
    Object.keys(this.markers).forEach(markerId => {
      this.removeMarker(markerId);
    });
  };

  changeMarkerIcon = (markerId, iconPath) => {
    if (this.markers[markerId]) {
      this.markers[markerId].setIcon(iconPath);
    }
  };

  changeMarkerLabel = (markerId, label) => {
    if (this.markers[markerId]) {
      this.markers[markerId].setLabel(label);
    }
  };

  getMarkerLabel = markerId =>
    this.markers[markerId] ? this.markers[markerId].getLabel() : null;

  changeMarkerZIndex = (markerId, zIndex) => {
    if (this.markers[markerId]) {
      this.markers[markerId].setZIndex(zIndex);
    }
  };

  setMarkerBounceAnimation = (markerId, timeout) => {
    if (this.markers[markerId]) {
      this.markers[markerId].setAnimation(this.googleMaps.Animation.BOUNCE);

      if (timeout) {
        setTimeout(() => {
          if (this.markers[markerId]) {
            this.markers[markerId].setAnimation(null);
          }
        }, timeout);
      }
    }
  };

  replaceMarkerListener = (markerId, eventName, callback) => {
    if (this.markers[markerId]) {
      this.googleMaps.event.clearListeners(this.markers[markerId], eventName);
      this.googleMaps.event.addListener(
        this.markers[markerId],
        eventName,
        callback
      );
    }
  };

  clearMarkerListeners = (markerId, eventName) => {
    if (this.markers[markerId]) {
      this.googleMaps.event.clearListeners(this.markers[markerId], eventName);
    }
  };

  addMapListener = (eventName, callback) => {
    this.map.addListener(eventName, callback);
  };

  removeMapListeners = () => {
    this.googleMaps.event.clearInstanceListeners(this.map);
  };

  addCircle = (circleId, opts) => {
    if (this.circles[circleId]) {
      this.removeCircle(circleId);
    }

    this.circles[circleId] = new this.googleMaps.Circle({
      strokeColor: opts.color,
      strokeOpacity: 0.4,
      strokeWeight: 1,
      fillColor: opts.color,
      fillOpacity: 0.15,
      map: this.map,
      center: {
        lat: parseFloat(opts.lat),
        lng: parseFloat(opts.lng)
      },
      radius: opts.radius
    });
  };

  removeCircle = circleId => {
    if (this.circles[circleId]) {
      this.circles[circleId].setMap(null);
      this.circles[circleId] = null;
    }
  };

  setCenter = opts => {
    this.map.setCenter({
      lat: parseFloat(opts.lat),
      lng: parseFloat(opts.lng)
    });
  };

  fitBoundsToMarkers = () => {
    const bounds = new this.googleMaps.LatLngBounds();
    forEach(this.markers, marker => {
      bounds.extend(marker.getPosition());
    });
    this.map.fitBounds(bounds);
  };

  fitBoundsToCoords = markersCoords => {
    const bounds = new this.googleMaps.LatLngBounds();
    if (markersCoords) {
      forEach(markersCoords, coords => {
        if (coords.lat && coords.lng) {
          bounds.extend(new this.googleMaps.LatLng(coords.lat, coords.lng));
        }
      });
    }
    this.map.fitBounds(bounds);
  };

  increaseZoom = () => {
    const currentZoom = this.map.getZoom();
    this.map.setZoom(currentZoom + 1);
  };

  setZoom = zoomValue => {
    this.map.setZoom(zoomValue);
  };

  getBounds = () => this.map.getBounds();

  coordsInBounds = (coords, mapPadding) => {
    const bounds = this.map.getBounds();
    const latlng = new this.googleMaps.LatLng(coords.lat, coords.lng);

    if (bounds && bounds.contains(latlng)) {
      if (!mapPadding) {
        return true;
      }

      const mapDiv = document.getElementById(this.mapId);
      if (!mapDiv) {
        return true;
      }
      const mapWidth = mapDiv.offsetWidth;
      const mapHeight = mapDiv.offsetHeight;

      // eslint-disable-next-line no-restricted-properties
      const scale = Math.pow(2, this.map.getZoom());

      const projection = this.map.getProjection();
      const coordsPoint = projection.fromLatLngToPoint(latlng);
      const norhtWestPoint = projection.fromLatLngToPoint(
        new this.googleMaps.LatLng(
          bounds.getNorthEast().lat(),
          bounds.getSouthWest().lng()
        )
      );

      const xOffset = Math.floor((coordsPoint.x - norhtWestPoint.x) * scale);
      const yOffset = Math.floor((coordsPoint.y - norhtWestPoint.y) * scale);

      const inXLimits =
        xOffset - mapPadding > 0 && xOffset + mapPadding < mapWidth;
      const inYLimits =
        yOffset - mapPadding > 0 && yOffset + mapPadding < mapHeight;
      if (inXLimits && inYLimits) {
        return true;
      }
    }

    return false;
  };
}

export default MapsAPI;
